import { GMGenericButton } from '..';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { ContactPreferenceButtonProps } from './ContactPreferenceButton.types';
import css from './ContactPreferenceButton.module.scss';
import { GMSizedIcon } from '../../Icon';
import classNames from 'classnames';
import { GMTitle } from '../../Title';
import { GMCapitalize } from '../../Case';
import { useField, useFormikContext } from 'formik';
import { useLocalization } from '../../../context/Localization/localization';
import { LocalizationType } from '../../../context/Localization/localization.types';
import { ContactPreferenceEnum } from '../../../context/Appointment/appointment';
import { IconTypes } from '../../Icon/GenericIcon/GenericIcon.types';
import { DetailsForm } from '../../../components/Appointment/Details/index.types';
import { GMGrid } from '../../Grid';
import { useEffect, useRef } from 'react';

/**
 * This control is used exclusively by the `CustomerDetails` and `TowInDetails` screen
 * to create the tiled contact preference buttons that the user can choose to
 * indicate how they want to be contacted by the dealership.
 *
 * When you pass in the `contactPreference` prop, the button programatically determines
 * the correct localization text label and the correct icon for the button.
 *
 * @param name - {@link ContactPreferenceButtonProps.name name} description.
 * @param contactPreference - {@link ContactPreferenceButtonProps.contactPreference contactPreference} description.
 * @param buttonSizeClassName - {@link ContactPreferenceButtonProps.buttonSizeClassName buttonSizeClassName} description.
 * @param className - {@link ContactPreferenceButtonProps.className className} description.
 * @param classes - {@link ContactPreferenceButtonProps.classes classes} description.
 * @param onClick - {@link ContactPreferenceButtonProps.onClick onClick} description.
 * @param noSubmit - {@link ContactPreferenceButtonProps.noSubmit noSubmit} description. (default: false)
 * @param smallButton - {@link ContactPreferenceButtonProps.smallButton smallButton} description. (default: false)
 */
const ContactPreferenceButton: GoMotoComponent<ContactPreferenceButtonProps> = ({
    name,
    contactPreference,
    buttonSizeClassName,
    className,
    classes,
    onClick,
    disabled,
    noSubmit = false,
    smallButton = false,
}) => {
    const [field] = useField<DetailsForm['contactPreference']>(name);
    const { setFieldValue, submitForm } = useFormikContext<DetailsForm>();
    const Localization: LocalizationType = useLocalization();

    const parentRef = useRef<HTMLHeadingElement>(null);
    const originalFontSize = smallButton ? 'calc(12px + 1vmin)' : 'calc(12px + 2vmin)';

    useEffect(() => {
        const handleResize = () => {
            if (parentRef?.current) {
                const parentWidth: number = parseFloat(window.getComputedStyle(parentRef.current).width);
                const textElement: HTMLParagraphElement = parentRef.current.getElementsByTagName('p')[0];

                textElement.style.fontSize = originalFontSize;
                while (parseFloat(window.getComputedStyle(textElement).width) > parentWidth) {
                    textElement.style.fontSize = `${parseFloat(window.getComputedStyle(textElement).fontSize) - 0.5}px`;
                }
            }
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [originalFontSize]);

    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, []);

    const getLabel: GoMotoFunction<[ContactPreferenceEnum], string> = (contactPreference) => {
        switch (contactPreference) {
            case ContactPreferenceEnum.Email:
                return Localization.Email;
            case ContactPreferenceEnum.Phone:
                return Localization.Phone;
            case ContactPreferenceEnum.Text:
                return Localization.Text;
            case ContactPreferenceEnum.Both:
                return Localization.Both;
        }
    };

    const getIconType: GoMotoFunction<[ContactPreferenceEnum], IconTypes | IconTypes[]> = (contactPreference) => {
        switch (contactPreference) {
            case ContactPreferenceEnum.Email:
                return 'Email';
            case ContactPreferenceEnum.Phone:
                return 'Phone';
            case ContactPreferenceEnum.Text:
                return 'Text';
            case ContactPreferenceEnum.Both:
                return ['Email', 'Text'];
        }
    };

    const label: string = getLabel(contactPreference);
    const contactIconType: IconTypes | IconTypes[] = getIconType(contactPreference);

    return (
        <GMGenericButton
            key={name + contactPreference}
            id={label}
            ariaLabel={Localization.formatString(Localization.SelectContactPreference, label) as string}
            onClick={onClick ? onClick : async () => {
                await setFieldValue(name, contactPreference);

                if (!noSubmit) {
                    await Promise.resolve();
                    submitForm();
                }
            }}
            className={className}
            disabled={disabled}
            classes={{
                ...classes,
            }}
        >
            <GMGrid
                id={`contact-pref-${label}-grid-container`}
                container
                className={classNames(
                    css.contactButton,
                    buttonSizeClassName,
                    field.value === contactPreference && css.selected,
                    smallButton ? css['smallPadding'] : css['defaultPadding'],
                    disabled && css.disabled,
                )}
            >
                {Array.isArray(contactIconType) ? (
                    <GMGrid
                        key={`contact-pref-${label}-icon-array-row`}
                        id={`contact-pref-${label}-icon-array-row`}
                        container
                        xs={12}
                        className={smallButton ? css.smallTwoIcons : css.contactIcon}
                    >
                        {contactIconType.map((iconType: IconTypes) => (
                            <GMSizedIcon
                                iconType={iconType}
                                iconSize={smallButton ? 'mediumLarge' : 'large'}
                                ariaHidden={true}
                            />
                        ))}
                    </GMGrid>
                ) : (
                    <GMGrid
                        key={`contact-pref-${label}-icon-row`}
                        id={`contact-pref-${label}-icon-row`}
                        xs={12}
                        className={smallButton ? css.smallOneIcon : css.contactIcon}
                    >
                        <GMSizedIcon
                            iconType={contactIconType}
                            iconSize={smallButton ? 'mediumLarge' : 'large'}
                            ariaHidden={true}
                        />
                    </GMGrid>
                )}
                <GMGrid
                    key={`contact-pref-${label}-title-row`}
                    id={`contact-pref-${label}-title-row`}
                    xs={12}
                    className={smallButton ? css.smallTitle : undefined}
                >
                    <GMTitle
                        headerLevel={6}
                        className={classNames(css.contactLabel, smallButton ? css.smallerTitle : null)}
                        ref={parentRef}
                    >
                        <GMCapitalize>{label}</GMCapitalize>
                    </GMTitle>
                </GMGrid>
            </GMGrid>
        </GMGenericButton>
    );
};

export default ContactPreferenceButton;